/**
 * 用户行为埋点工具
 *
 * 功能：
 * - 访客ID生成和持久化（Cookie + localStorage 双重存储）
 * - 页面浏览埋点
 * - 点击事件埋点
 */

import { reportBehavior } from '@/api/analytics'

// ========== 常量定义 ==========

// 客户端类型枚举
const ClientType = {
  CUST_APP: '春信助教',       // 客户端APP
  FREE_APP: '春信管理版',     // 教练端APP
  H5: 'H5',                  // H5网页
  MINI: 'MINI',               // 微信小程序
  WEB: 'WEB'                 // PC网页
}

// 事件类型枚举
const EventType = {
  PAGEVIEW: 'pageview',      // 页面浏览
  CLICK: 'click'             // 点击事件
}

// Cookie 相关常量
const VISITOR_ID_COOKIE_KEY = 'visitor_id'
const COOKIE_EXPIRY_DAYS = 730  // 2年过期

// 路径到页面标识的映射（与插件保持一致）
const PATH_TO_PAGE_MAP = {
  '/': 'home',
  '/splash': 'splash',
  '/recommended': 'home',
  '/program': 'program',
  '/coaches': 'partner_list',
  '/coach-detail': 'partner_detail',
  '/conversations': 'chat_list',
  '/conversation-detail': 'chat_message',
  '/video': 'video',
  '/messages/center': 'message_center',
  '/messages/list': 'message_list',
  '/profile': 'my',
  '/wellness': 'wellness',
  '/portfolio/detail': 'portfolio_detail',
  '/portfolio/topics': 'portfolio_topic',
  '/login': 'login',
  '/mobile-login': 'mobile_login',
  '/cities': 'city_list'
}

// 当前页面标识（由插件更新）
let currentPage = 'home'

/**
 * 设置当前页面（由插件调用）
 * @param {string} page - 页面标识
 */
export function setCurrentPage(page) {
  currentPage = page
}

// ========== 私有方法 ==========

/**
 * 生成UUID v4
 * @returns {string} UUID
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * 设置Cookie
 * @param {string} name - Cookie名称
 * @param {string} value - Cookie值
 * @param {number} days - 过期天数
 */
function setCookie(name, value, days) {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    expires = '; expires=' + date.toUTCString()
  }
  document.cookie = name + '=' + value + expires + '; path=/'
}

/**
 * 获取Cookie
 * @param {string} name - Cookie名称
 * @returns {string|null} Cookie值
 */
function getCookie(name) {
  const nameEQ = name + '='
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i]
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length)
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length)
    }
  }
  return null
}

/**
 * 获取当前客户端类型
 * @returns {string} 客户端类型
 */
function getClientType() {
  // 优先从环境变量读取（打包时注入）
  const envClientType = import.meta.env.VITE_CLIENT_TYPE
  if (envClientType) {
    return envClientType
  }

  // 根据 UA 或平台特征判断
  const ua = navigator.userAgent.toLowerCase()

  // 判断是否在 Capacitor APP 中
  if (window.Capacitor) {
    // 根据应用配置判断是客户端还是教练端
    // 这里需要根据实际项目配置调整
    return ClientType.CUST_APP  // 默认客户端，可配置
  }

  // 判断微信小程序
  if (window.__wxjs_environment === 'miniprogram' || ua.indexOf('miniprogram') > -1) {
    return ClientType.MINI
  }

  // 判断微信H5
  if (ua.indexOf('micromessenger') > -1) {
    return ClientType.H5
  }

  // 默认H5
  return ClientType.H5
}

/**
 * 获取访客ID（Cookie + localStorage 双重存储）
 * @returns {string} 访客ID
 */
function getVisitorId() {
  // 1. 优先从 Cookie 读取
  let visitorId = getCookie(VISITOR_ID_COOKIE_KEY)

  // 2. Cookie 没有则从 localStorage 读取
  if (!visitorId) {
    visitorId = localStorage.getItem(VISITOR_ID_COOKIE_KEY)
  }

  // 3. 都没有则生成新的
  if (!visitorId) {
    visitorId = generateUUID()
    // 双重存储
    setCookie(VISITOR_ID_COOKIE_KEY, visitorId, COOKIE_EXPIRY_DAYS)
    localStorage.setItem(VISITOR_ID_COOKIE_KEY, visitorId)
    console.log('[Analytics] 生成新访客ID:', visitorId)
  }

  // 4. 同步 Cookie 和 localStorage（确保一致性）
  const cookieVisitorId = getCookie(VISITOR_ID_COOKIE_KEY)
  const localVisitorId = localStorage.getItem(VISITOR_ID_COOKIE_KEY)

  if (cookieVisitorId && localVisitorId !== cookieVisitorId) {
    localStorage.setItem(VISITOR_ID_COOKIE_KEY, cookieVisitorId)
  } else if (localVisitorId && (!cookieVisitorId || cookieVisitorId !== localVisitorId)) {
    setCookie(VISITOR_ID_COOKIE_KEY, localVisitorId, COOKIE_EXPIRY_DAYS)
  }

  return visitorId
}

/**
 * 统一上报行为数据
 * @param {Object} data - 行为数据
 * @param {string} data.eventType - 事件类型（pageview/click）
 * @param {string} data.page - 页面标识
 * @param {string} [data.element] - 点击控件
 * @param {Object} [data.bizExt] - 业务扩展字段
 */
async function sendBehavior(data) {
  try {
    const visitorId = getVisitorId()
    const clientType = getClientType()

    // 获取 token（从 localStorage，与 axios 保持一致）
    const token = localStorage.getItem('token')

    const payload = {
      visitorId,
      eventType: data.eventType,
      page: data.page,
      element: data.element || '',
      clientType,
      reqUrl: window.location.href,
      bizExt: data.bizExt ? JSON.stringify(data.bizExt) : null
    }

    console.log('[Analytics] 准备上报行为:', payload, 'token:', token ? '已携带' : '未登录')

    // 使用 fetch 上报（可以获取响应状态）
    const headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers['token'] = token
    }

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/behavior/v1/report`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    if (response.ok) {
      const result = await response.json()
      console.log('[Analytics] 行为上报成功:', payload, result)
    } else {
      console.error('[Analytics] 行为上报失败:', response.status, response.statusText)
    }
  } catch (error) {
    console.error('[Analytics] 行为上报异常:', error)
  }
}

// ========== 公开方法 ==========

/**
 * 页面浏览埋点
 * @param {string} page - 页面标识（如 'home', 'partner_detail'）
 * @param {Object} [bizExt] - 业务扩展字段
 * @param {string} [bizExt.refType] - 关联类型（如 'partner', 'booking', 'package'）
 * @param {number} [bizExt.refId] - 关联ID
 */
export function trackPageView(page, bizExt = {}) {
  sendBehavior({
    eventType: EventType.PAGEVIEW,
    page,
    bizExt
  })
}

/**
 * 从 URL 中提取页面标识（支持 hash 路由）
 * @returns {string} 页面标识
 */
function getCurrentPageFromUrl() {
  // 优先从 hash 中提取路径（hash 模式）
  let path = ''
  if (window.location.hash) {
    path = window.location.hash.split('?')[0]  // 移除查询参数
      .replace('#/', '')          // 移除 #/
      .replace('#', '')           // 移除单个 #
  } else {
    path = window.location.pathname
  }

  // 移除开头的斜杠并标准化
  if (path.startsWith('/')) {
    path = path.substring(1)
  }

  // 移除路径中的数字（如 ID）
  path = path.replace(/\/\d+/g, '')

  // 查找映射
  if (PATH_TO_PAGE_MAP['/' + path]) {
    return PATH_TO_PAGE_MAP['/' + path]
  }

  // 默认转换：将斜杠和连字符替换为下划线
  return path.replace(/\//g, '_').replace(/-/g, '_').toLowerCase() || 'home'
}

/**
 * 点击事件埋点
 * @param {string} element - 点击控件标识（如 'button_submit_booking', 'nav_home'）
 * @param {Object} [bizExt] - 业务扩展字段
 */
export function trackClick(element, bizExt = {}) {
  // 优先使用插件设置的当前页面，否则从 URL 提取
  const page = currentPage || getCurrentPageFromUrl()
  sendBehavior({
    eventType: EventType.CLICK,
    page,
    element,
    bizExt
  })
}

/**
 * 手动上报行为（高级用法）
 * @param {Object} data - 完整行为数据
 */
export function trackBehavior(data) {
  sendBehavior(data)
}

/**
 * 获取当前访客ID（供调试使用）
 * @returns {string} 访客ID
 */
export function getVisitorIdForDebug() {
  return getVisitorId()
}

/**
 * 获取当前客户端类型（供调试使用）
 * @returns {string} 客户端类型
 */
export function getClientTypeForDebug() {
  return getClientType()
}

// ========== 导出常量 ==========

export { ClientType, EventType }

// ========== 默认导出 ==========

export default {
  trackPageView,
  trackClick,
  trackBehavior,
  getVisitorId: getVisitorIdForDebug,
  getClientType: getClientTypeForDebug,
  ClientType,
  EventType
}
